import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Facebook,
  Instagram,
  Twitter,
  Youtube,
  Globe,
  Mail,
  User,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOCIAL_META = {
  facebook: { Icon: Facebook, label: "Facebook" },
  facebook_personal: { Icon: Facebook, label: "Facebook (Personal)" },
  instagram: { Icon: Instagram, label: "Instagram" },
  twitter: { Icon: Twitter, label: "X / Twitter" },
  youtube: { Icon: Youtube, label: "YouTube" },
  website: { Icon: Globe, label: "Website" },
  email: { Icon: Mail, label: "Email" },
};

const DAY_LABELS = {
  sunday: "SUN",
  monday: "MON",
  tuesday: "TUE",
  wednesday: "WED",
  thursday: "THU",
  friday: "FRI",
  saturday: "SAT",
};

const FULL_DAY_LABELS = {
  sunday: "SUNDAY",
  monday: "MONDAY",
  tuesday: "TUESDAY",
  wednesday: "WEDNESDAY",
  thursday: "THURSDAY",
  friday: "FRIDAY",
  saturday: "SATURDAY",
};

function getTodayKey() {
  const idx = new Date().getDay(); // 0=Sun..6=Sat
  return [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ][idx];
}

export default function Schedule() {
  const [days, setDays] = useState([]);
  const [schedule, setSchedule] = useState({});
  const [activeDay, setActiveDay] = useState(getTodayKey());
  const today = useMemo(() => getTodayKey(), []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/schedule`);
        if (cancelled) return;
        setDays(res.data.days || []);
        setSchedule(res.data.schedule || {});
      } catch (e) {
        /* silent */
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const shows = schedule[activeDay] || [];

  return (
    <section
      id="schedule"
      className="relative border-t border-zinc-800 bg-zinc-950"
      data-testid="schedule-section"
    >
      <div className="px-4 md:px-10 py-12 md:py-20">
        {/* Heading */}
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="text-xs tracking-[0.4em] text-zinc-300 uppercase mb-3">
              //_BROADCAST_SCHEDULE
            </div>
            <h2 className="font-display text-5xl md:text-7xl leading-none text-zinc-50 glitch">
              THE LINEUP<span className="text-red-600">.</span>
            </h2>
          </div>
          <div className="text-sm text-zinc-300 max-w-md">
            Every day on Digital Revolution Radio. Click any day below to see
            its shows.
          </div>
        </div>

        {/* Legend — one-line key explaining the LIVE badge */}
        <div
          className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2"
          data-testid="schedule-legend"
        >
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 bg-red-600 text-black px-2 py-0.5 font-display">
              <span className="w-1.5 h-1.5 rounded-full bg-black animate-pulse" />
              LIVE
            </span>
            <span>= host on the mic right now</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-zinc-300">//_unlabeled</span>
            <span>= pre-recorded mix</span>
          </div>
        </div>

        {/* Day tabs */}
        <div
          className="grid grid-cols-7 border border-zinc-800 mb-0"
          data-testid="schedule-day-tabs"
        >
          {days.map((d) => {
            const isActive = d === activeDay;
            const isToday = d === today;
            return (
              <button
                key={d}
                type="button"
                onClick={() => setActiveDay(d)}
                className={`relative py-4 px-2 font-display text-lg md:text-2xl tracking-tight uppercase border-r border-zinc-800 last:border-r-0 transition-colors ${
                  isActive
                    ? "bg-red-600 text-black"
                    : "bg-zinc-950 text-zinc-300 hover:bg-zinc-100 hover:text-black"
                }`}
                data-testid={`schedule-tab-${d}`}
              >
                <span className="hidden sm:inline">{DAY_LABELS[d]}</span>
                <span className="sm:hidden">{DAY_LABELS[d][0]}</span>
                {isToday && (
                  <span
                    className={`absolute top-1 right-1 text-[9px] tracking-widest ${
                      isActive ? "text-black" : "text-red-500"
                    }`}
                  >
                    ●
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Day banner */}
        <div className="border-x border-b border-zinc-800 px-4 md:px-6 py-3 flex items-center justify-between bg-zinc-900/60">
          <div className="font-display text-2xl md:text-3xl tracking-tight">
            {FULL_DAY_LABELS[activeDay] || activeDay.toUpperCase()}
            {activeDay === today && (
              <span className="text-xs text-red-500 tracking-[0.3em] uppercase ml-3">
                · TODAY
              </span>
            )}
          </div>
          <div className="text-xs tracking-widest text-zinc-300 uppercase">
            {shows.length} show{shows.length === 1 ? "" : "s"}
          </div>
        </div>

        {/* Show cards for selected day */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 border-l border-t border-zinc-800"
          data-testid="schedule-cards"
        >
          {shows.length === 0 ? (
            <div className="col-span-full p-10 text-zinc-300 text-sm tracking-widest uppercase border-r border-b border-zinc-800">
              //_no shows scheduled for {FULL_DAY_LABELS[activeDay]}
            </div>
          ) : (
            shows.map((show, idx) => (
              <div
                key={`${activeDay}-${idx}-${show.time}`}
                className="relative border-r border-b border-zinc-800 p-5 md:p-6 min-h-[180px] flex flex-col justify-between group transition-colors bg-zinc-950 hover:bg-zinc-100 hover:text-black"
                data-testid={`schedule-card-${activeDay}-${idx}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="font-display text-xl md:text-2xl tracking-tight text-zinc-100 group-hover:text-black">
                    {show.time}
                  </div>
                  {show.live && (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase bg-red-600 text-black px-2 py-0.5 font-display whitespace-nowrap group-hover:bg-black group-hover:text-red-600"
                      data-testid="schedule-live-badge"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-black animate-pulse group-hover:bg-red-600" />
                      LIVE
                    </span>
                  )}
                </div>
                <div className="mt-4">
                  <div className="font-display text-xl md:text-2xl leading-tight mb-1 tracking-tight">
                    {show.name}
                  </div>
                  {show.dj_name && (
                    <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-300 group-hover:text-zinc-700 mb-3">
                      //_host:{" "}
                      {show.dj_slug ? (
                        <Link
                          to={`/djs/${show.dj_slug}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-red-500 hover:text-red-700 group-hover:text-red-600"
                          data-testid={`schedule-host-name-${show.dj_slug}`}
                        >
                          {show.dj_name}
                        </Link>
                      ) : (
                        <span className="text-zinc-200 group-hover:text-zinc-800">
                          {show.dj_name}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-2 flex-wrap">
                    {show.dj_slug && (
                      <Link
                        to={`/djs/${show.dj_slug}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-[10px] tracking-widest uppercase inline-flex items-center gap-1 border border-zinc-700 group-hover:border-black px-2 py-1 hover:!bg-red-600 hover:!text-white hover:!border-red-600 transition-colors"
                        data-testid={`schedule-dj-link-${show.dj_slug}`}
                      >
                        <User className="w-3 h-3" /> Meet DJ
                      </Link>
                    )}
                    <SocialIcons socials={show.socials} showKey={show.key} />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="mt-8 text-xs text-zinc-600 tracking-widest uppercase">
          //_times shown in station local time · subject to change
        </div>
      </div>
    </section>
  );
}

function SocialIcons({ socials, showKey }) {
  if (!socials) return null;
  const entries = Object.entries(socials).filter(
    ([k, v]) => v && SOCIAL_META[k]
  );
  if (entries.length === 0) return null;
  return (
    <div
      className="mt-2 flex flex-wrap items-center gap-2"
      data-testid={`schedule-socials-${showKey}`}
    >
      {entries.map(([platform, url]) => {
        const { Icon, label } = SOCIAL_META[platform];
        return (
          <a
            key={platform}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            title={label}
            aria-label={label}
            onClick={(e) => e.stopPropagation()}
            className="w-7 h-7 inline-flex items-center justify-center border border-zinc-700 text-zinc-100 group-hover:text-black group-hover:border-black hover:!bg-red-600 hover:!text-white hover:!border-red-600 transition-colors"
            data-testid={`social-link-${showKey}-${platform}`}
          >
            <Icon className="w-3.5 h-3.5" />
          </a>
        );
      })}
    </div>
  );
}
